import { defineStore } from 'pinia'

export type Language = 'de' | 'en' | 'debug'
export type DateLocale = 'de-DE' | 'en-US' | 'scientific'
export type Theme = 'light' | 'dark'

interface SettingsState {
  theme: Theme
  language: Language
  dateLocale: DateLocale
  genericInstancesEnabled: boolean
  loadBarnImages: boolean
  showVersions: boolean
  showConnectionInfo: boolean
  showTypeInformation: boolean
  autoAddServiceProviders: boolean
  enableAutoLogin: boolean
  serviceProvidersExcludedFromAutoAdd: string[]
  showCameraIdsOnGridView: boolean
  languageSelected: boolean
  forcedConnectionStrategy: string | null
  forceConnectionStrategy: boolean
  enableDeveloperSettings: boolean
  instanceListDisplayMode: 'grid' | 'list'
  useAlignmentHelperAsImageReplacement: boolean
  showFrameAnnotationTour: boolean
  simulatePermissions: boolean
  simulatedPermissions: string[]
  queueDebugOutput: boolean
}

export const useSettingsStore = defineStore('localSettings', {
  state: (): SettingsState => ({
    theme: 'light',
    language: 'de',
    dateLocale: 'de-DE',
    genericInstancesEnabled: false,
    loadBarnImages: false,
    showVersions: false,
    showConnectionInfo: false,
    showTypeInformation: false,
    autoAddServiceProviders: true,
    enableAutoLogin: true,
    serviceProvidersExcludedFromAutoAdd: [],
    showCameraIdsOnGridView: false,
    languageSelected: false,
    forcedConnectionStrategy: null,
    forceConnectionStrategy: false,
    enableDeveloperSettings: false,
    instanceListDisplayMode: 'grid',
    useAlignmentHelperAsImageReplacement: false,
    showFrameAnnotationTour: true,
    simulatePermissions: false,
    simulatedPermissions: [],
    queueDebugOutput: false,
  }),
  persist: true,
  share: {
    enable: true,
    initialize: false,
  },
  getters: {
    isPermissionSimulationEnabled: state => state.simulatePermissions === true,
    getSimulatedPermissions: state => (state.simulatePermissions === true ? state.simulatedPermissions : null),
  },
  actions: {
    setShowFrameAnnotationTour(value: boolean) {
      this.showFrameAnnotationTour = value
    },
    setTheme(theme: Theme) {
      this.theme = theme
      const themeMeta = document.querySelector('meta[name="theme-color"]')
      if (themeMeta) themeMeta.setAttribute('content', '#212121')
    },
    setLanguage(language: Language) {
      this.languageSelected = true
      this.language = language
    },
    setDateLocale(dateLocale: DateLocale) {
      this.dateLocale = dateLocale
    },
    setForcedConnectionStrategy(strategy: string) {
      this.forcedConnectionStrategy = strategy
    },
    toggleSimulatePermissions() {
      this.simulatePermissions = !this.simulatePermissions
    },
    setSimulatedPermissions(permissions: string[]) {
      this.simulatedPermissions = [...permissions]
    },
    disableQueueDebugOutput() {
      this.queueDebugOutput = false
    },
  },
})
